import React, { useState, useEffect, useMemo, useRef } from 'react';
import ModalComponent from '../../common/Modal/modal';
import { CustomButton } from '../../../widgets';
import {
  fetchCitationStyles,
  fetchStyleXML,
  getActiveStyle,
  setActiveStyle,
} from '../services/CitationService/CitationStyleService';
import './CitationStyleSelector.scss';

/**
 * CitationStyleSelector
 *
 * Modal that lets the user search & select a CSL citation style.
 * Styles are fetched from the backend API on open.
 *
 * Props:
 *  visible  {boolean}
 *  onClose  {function}
 *  onStyleChange {function(styleObj)} – called after a style is successfully applied
 */
const CitationStyleSelector = ({ visible, onClose, onStyleChange }) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stylesLoading, setStylesLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStyle, setCurrentStyle] = useState(getActiveStyle());
  const [styles, setStyles] = useState([]);
  const inputRef = useRef(null);

  // Initialize on modal open
  useEffect(() => {
    if (visible) {
      setCurrentStyle(getActiveStyle());
      setSelected(null);
      setError('');
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [visible]);

  const handleSearch = () => {
    if (!query.trim()) {
      setStyles([]);
      return;
    }
    loadStyles(query);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const loadStyles = async (searchQuery) => {
    setStylesLoading(true);
    setError('');
    try {
      const data = await fetchCitationStyles(searchQuery);
      setStyles(data || []);
      if (data && data.length === 0) {
        setError('No styles found for your search.');
      }
    } catch (err) {
      setError('Failed to load citation styles.');
      console.error('Failed to load citation styles:', err);
    } finally {
      setStylesLoading(false);
    }
  };

  // We no longer filter locally, we just use what the API returns
  const filtered = styles;

  const handleApply = async () => {
    if (!selected) return;
    if (selected.citation_id === currentStyle.id) {
      onClose();
      return;
    }

    setLoading(true);
    setError('');
    try {
      // Fetch the CSL XML from backend (cached in memory)
      await fetchStyleXML(selected.citation_id);
      // Build the style object to persist (id = citation_id for internal use)
      const styleObj = {
        id: selected.citation_id,
        label: selected.title || selected.short_title || selected.citation_id,
      };
      setActiveStyle(styleObj);
      setCurrentStyle(styleObj);
      if (onStyleChange) onStyleChange(styleObj);
      // Trigger bibliography + citation refresh
      window.dispatchEvent(new Event('referencesUpdated'));
      window.dispatchEvent(new CustomEvent('citationStyleChanged', { detail: styleObj }));
      window.dispatchEvent(new Event('forceCitationSync'));
      onClose();
    } catch (err) {
      setError(`Could not load style: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalComponent
      header="Citation Style"
      isOpen={visible}
      onClose={onClose}
      width="680px"
      customClass="citation-style-selector-modal"
      footer={
        <div className="css-footer">
          <span className="css-active-badge">
            Active: <strong>{currentStyle.label || currentStyle.title || currentStyle.id}</strong>
          </span>
          <div className="css-footer-actions">
            <CustomButton type="secondary" text="Cancel" onClick={onClose} />
            <CustomButton
              type="primary"
              text={loading ? 'Applying…' : 'Apply Style'}
              onClick={handleApply}
              disabled={!selected || loading}
            />
          </div>
        </div>
      }
      body={
        <div className="css-body">
          {/* Search */}
          <div className="css-search-wrap">
            <span className="css-search-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              ref={inputRef}
              className="css-search-input"
              type="text"
              placeholder="Search styles (e.g. APA, IEEE, Harvard…)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {query && (
              <button className="css-clear-btn" onClick={() => { setQuery(''); setStyles([]); }}>×</button>
            )}
            <button 
              className="css-search-btn" 
              onClick={handleSearch}
              disabled={!query.trim() || stylesLoading}
            >
              Search
            </button>
          </div>

          {/* Error */}
          {error && <div className="css-error">{error}</div>}

          {/* Loading indicator */}
          {stylesLoading && (
            <div className="css-loading" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
              Loading citation styles...
            </div>
          )}

          {/* Style list */}
          {!stylesLoading && (
            <div className="css-list">
              {filtered.length === 0 && (
                <div className="css-empty">No styles match your search.</div>
              )}
              {filtered.map((style) => {
                const isActive  = style.citation_id === currentStyle.id;
                const isChosen  = selected?.citation_id === style.citation_id;
                const displayLabel = style.title || style.short_title || style.citation_id;
                return (
                  <div
                    key={style.citation_id}
                    className={`css-item ${isChosen ? 'css-item--selected' : ''} ${isActive ? 'css-item--active' : ''}`}
                    onClick={() => setSelected(style)}
                  >
                    <div className="css-item-radio">
                      <div className={`css-radio-dot ${isChosen || isActive ? 'css-radio-dot--on' : ''}`} />
                    </div>
                    <div className="css-item-meta">
                      <span className="css-item-label">{displayLabel}</span>
                      <span className="css-item-id">{style.format ? `${style.citation_id} • ${style.format}` : style.citation_id}</span>
                    </div>
                    {isActive && <span className="css-badge css-badge--active">Active</span>}
                    {isChosen && !isActive && <span className="css-badge css-badge--selected">Selected</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      }
    />
  );
};

export default CitationStyleSelector;
